import React, { useEffect, useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import {
  LoggedOutEmptyState,
  NoFavoritesEmptyState,
} from 'features/menu/pages/favorites-page/favorites-card-list/empty-state';
import { FavoriteCard } from 'features/menu/pages/favorites-page/favorites-card-list/favorite-card';
import { FavoriteCardData } from 'features/menu/pages/favorites-page/favorites-card-list/types';
import { MinimalFavoriteCard } from 'features/menu/pages/favorites-page/minimal-favorites/minimal-favorite-card';
import { useMinimalFavorites } from 'features/menu/pages/favorites-page/minimal-favorites/use-minimal-favorites';
import { useStoreContext } from 'features/store';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import { toaster } from 'components/toaster/toaster';
import { useExposeMinimalCartExperiment } from 'experiments/use-enable-minimal-cart-experiment';
import {
  GetUserFavoritesRefsDocument,
  GetUserMinimalFavoritesDocument,
  GetUserReorderFavoritesDocument,
  useDeleteFavoriteMutation,
  useDeleteMinimalFavoriteMutation,
  useGetUserReorderFavoritesQuery,
} from 'generated/graphql-gateway';
import useFoodware from 'hooks/use-foodware';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import { useAuthContext } from 'state/auth';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { spacing } from 'styles/constants';
import { routes } from 'utils/routing';

/**
 *
 * FavoritesCardList displays all the favorite orders and allows the guest to reorder, edit, and remove
 *
 */
const InnerFavoritesCardList = () => {
  const [removeFavoriteParams, setRemoveFavoriteParams] = React.useState<{
    id: string;
  } | null>(null);

  const enableMinimalCartUi = useMinimalCartUi();
  useExposeMinimalCartExperiment();
  const { data, loading, refetch } = useGetUserReorderFavoritesQuery({
    skip: enableMinimalCartUi,
    fetchPolicy: 'cache-and-network',
  });
  const {
    favorites: minimalFavorites,
    loading: minimalFavoritesLoading,
    refetch: refetchMinimalFavorites,
  } = useMinimalFavorites(enableMinimalCartUi);

  const [deleteFavorite] = useDeleteFavoriteMutation({
    onCompleted: () => toaster.positive(t('removeFavoriteSucess')),
  });
  const [deleteMinimalFavorite] = useDeleteMinimalFavoriteMutation({
    onCompleted: () => toaster.positive(t('removeFavoriteSucess')),
  });

  const { navigate } = useRouter();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected } = useStoreContext();

  const { setFavorite, shouldRefetchFavorites, setShouldRefetchFavorites } = useFavoritesContext();

  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const handleEditFavorite = (id: string) =>
    navigate(noStoreSelected ? routes.storeLocator : `${routes.favorites}/${id}/edit`);

  const handleRemoveFavorite = (id: string) => {
    setRemoveFavoriteParams({ id });
  };

  const favorites = data?.userFavorites?.favorites;
  const shouldRefetch = enableMinimalCartUi
    ? shouldRefetchFavorites
    : !favorites || shouldRefetchFavorites;

  useEffect(() => {
    setFavorite(null);
  }, [favorites, minimalFavorites, setFavorite]);

  useEffect(() => {
    if (shouldRefetch) {
      if (enableMinimalCartUi) {
        refetchMinimalFavorites();
      } else {
        refetch();
      }
      setShouldRefetchFavorites(false);
    }
  }, [
    enableMinimalCartUi,
    refetch,
    refetchMinimalFavorites,
    setShouldRefetchFavorites,
    shouldRefetch,
  ]);

  const eligibleFavoriteOrders = useMemo(() => {
    if (enableMinimalCartUi) {
      return minimalFavorites?.filter(favorite => (favorite.minimalEntries?.length ?? 0) > 0);
    }

    return favorites?.reduce((favsAccum: FavoriteCardData[], favorite) => {
      const entries = (favorite?.entries || ([] as unknown)) as IBackendCartEntries[];

      const isFavoriteOrderReorderable = entries.some(entry =>
        isCartEntryReorderable(entry, sanityIdsExcludedFromReordering, isFoodware)
      );

      if (isFavoriteOrderReorderable) {
        favsAccum.push({
          id: favorite?._id || '',
          name: favorite?.name || '',
          ref: favorite?.ref,
          entries,
        });
      }

      return favsAccum;
    }, []);
  }, [
    enableMinimalCartUi,
    favorites,
    isFoodware,
    minimalFavorites,
    sanityIdsExcludedFromReordering,
  ]);

  const removeFavorite = () => {
    if (removeFavoriteParams?.id) {
      if (enableMinimalCartUi) {
        deleteMinimalFavorite({
          variables: {
            favoriteId: removeFavoriteParams.id,
          },
          refetchQueries: [
            { query: GetUserMinimalFavoritesDocument },
            { query: GetUserFavoritesRefsDocument },
          ],
        });
      } else {
        deleteFavorite({
          variables: {
            favoriteId: removeFavoriteParams.id,
          },
          refetchQueries: [
            { query: GetUserReorderFavoritesDocument },
            { query: GetUserFavoritesRefsDocument },
          ],
        });
      }
    }
    setRemoveFavoriteParams(null);
  };

  if (loading || minimalFavoritesLoading) {
    return <ProductCardListSkeleton hasFooter />;
  }

  if (!eligibleFavoriteOrders?.length) {
    if (!isAuthenticated) {
      return <LoggedOutEmptyState />;
    }
    return <NoFavoritesEmptyState />;
  }

  return (
    <>
      {eligibleFavoriteOrders?.map((favorite, i) => {
        if (enableMinimalCartUi) {
          return (
            <MinimalFavoriteCard
              key={i}
              id={favorite.id}
              name={favorite.name}
              favoriteRef={favorite.ref}
              minimalEntries={favorite.minimalEntries ?? []}
              onEditFavorite={handleEditFavorite}
              onRemoveFavorite={handleRemoveFavorite}
            />
          );
        }

        return (
          <FavoriteCard
            key={i}
            id={favorite.id}
            name={favorite.name}
            favoriteRef={favorite.ref}
            entries={favorite.entries}
            onEditFavorite={handleEditFavorite}
            onRemoveFavorite={handleRemoveFavorite}
          />
        );
      })}

      {!!removeFavoriteParams && (
        <ConfirmDialog
          showDialog
          heading={t('removeFavorite')}
          body={t('removeFavoriteConfirm')}
          cancelLabel={t('cancel')}
          confirmLabel={t('remove')}
          onCancel={() => setRemoveFavoriteParams(null)}
          onConfirm={removeFavorite}
          modalAppearanceEventMessage="Remove Favorite"
        />
      )}
    </>
  );
};

const FavoritesCardList = () => {
  const nativeTabBarContentInset = useNativeTabBarContentInset();

  return (
    <ScrollView
      testID="menu-favorites-page"
      style={{ marginTop: spacing.$1 }}
      contentContainerStyle={{ paddingBottom: nativeTabBarContentInset }}
    >
      <CenteredRowContainer>
        <InnerFavoritesCardList />
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default withMenuEmptyState(FavoritesCardList);
